import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Plus, X, Globe, Settings, Eye, EyeOff } from 'lucide-react';
import api from '../services/api';
import MapPicker from './MapPicker';
import { GoogleMap, Polygon, useJsApiLoader } from '@react-google-maps/api';
import useDefaultMapConfig from '../hooks/useDefaultMapConfig';
import HeatmapConfigManager from './HeatmapConfigManager';

const GOOGLE_MAPS_LIBRARIES = [];

// Helper: Calculate centroid of a polygon (array of [lng, lat])
function getPolygonCentroid(coords) {
  // Only use the outer ring (first array)
  const points = coords[0];
  let x = 0,
    y = 0;
  const n = points.length;
  points.forEach(([lng, lat]) => {
    x += lng;
    y += lat;
  });
  return { lat: y / n, lng: x / n };
}

function AreaConfigManager({ onActiveAreaChange }) {
  const [areas, setAreas] = useState([]);
  const [activeArea, setActiveArea] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [formData, setFormData] = useState({
    area_type: 'electorate',
    area_name: '',
    area_code: '',
    coordinates: [],
    center_lat: '',
    center_lng: '',
    zoom_level: 12,
    border_strictness: 'medium',
  });
  const [electorates, setElectorates] = useState([]); // List of all electorate features
  const [selectedElectorate, setSelectedElectorate] = useState(null); // Feature
  const [electorateSearch, setElectorateSearch] = useState('');

  // Map restrictions state
  const [mapRestrictions, setMapRestrictions] = useState({
    enabled: true,
    bounds: {
      north: -37.73,
      south: -37.74,
      east: 145.005,
      west: 144.96,
    },
    strictBounds: false,
    zoomLevel: 13,
  });
  const [showMapRestrictions, setShowMapRestrictions] = useState(false);

  // Google Maps API
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey:
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyDfJ-ekd6yt6eT6G2lkRDE4-R5rpxq1jLU',
    version: 'weekly',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  const { defaultCenter, defaultZoom, reloadDefaults } = useDefaultMapConfig();
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(defaultZoom);
  const mapRef = useRef(null);
  const formRef = useRef(null);

  const loadAreas = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.getAreaConfig();
      setAreas(response.areas || []);
      setActiveArea(response.activeArea);

      // Load map restrictions from app config
      const appConfigResponse = await api.getAppConfig();
      if (appConfigResponse.config && appConfigResponse.config.mapRestrictions) {
        setMapRestrictions(appConfigResponse.config.mapRestrictions);
      }
    } catch (err) {
      console.error('Error loading areas:', err);
      setError('Failed to load area configurations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAreas();
    // Load electorates GeoJSON from backend API (more efficient)
    api
      .get('/config/electorates-geojson')
      .then(response => {
        const data = response;
        setElectorates(data.features || []);
        // Default to Pascoe Vale
        const pascoe = (data.features || []).find(
          f => (f.properties.vic_stat_2 || '').toLowerCase() === 'pascoe vale',
        );
        const defaultElectorate = pascoe || (data.features ? data.features[0] : null);
        setSelectedElectorate(defaultElectorate);
        if (
          defaultElectorate &&
          defaultElectorate.geometry &&
          defaultElectorate.geometry.coordinates
        ) {
          const centroid = getPolygonCentroid(defaultElectorate.geometry.coordinates);
          setMapCenter(centroid);
        }
        // Set as active area in parent if callback provided
        if (onActiveAreaChange && defaultElectorate) {
          onActiveAreaChange(defaultElectorate);
        }
      })
      .catch(err => {
        console.error('Error loading electorate data:', err);
        setError('Failed to load electorate data');
      });
  }, []);

  // When selectedElectorate changes, fit map to bounds
  // useEffect(() => {
  //   if (mapRef.current && selectedElectorate && selectedElectorate.geometry && selectedElectorate.geometry.coordinates) {
  //     const bounds = getPolygonBounds(selectedElectorate.geometry.coordinates);
  //     console.log('Electorate bounds:', bounds);
  //     const google = window.google;
  //     if (google && google.maps && mapRef.current.fitBounds) {
  //       if (
  //         isNaN(bounds.south) || isNaN(bounds.north) || isNaN(bounds.east) || isNaN(bounds.west) ||
  //         bounds.south === bounds.north || bounds.east === bounds.west
  //       ) {
  //         // Invalid or degenerate bounds, fallback to default
  //         console.warn('Invalid bounds, using default center/zoom');
  //         mapRef.current.setCenter({ lat: -37.735438, lng: 144.972502 });
  //         mapRef.current.setZoom(12);
  //       } else {
  //         const gBounds = new google.maps.LatLngBounds(
  //           { lat: bounds.south, lng: bounds.west },
  //           { lat: bounds.north, lng: bounds.east }
  //         );
  //         // Add padding (in pixels)
  //         mapRef.current.fitBounds(gBounds, { top: 50, bottom: 50, left: 50, right: 50 });
  //         // If bounds are very small, set a minimum zoom
  //         const listener = google.maps.event.addListenerOnce(mapRef.current, 'idle', () => {
  //           const currentZoom = mapRef.current.getZoom();
  //           if (currentZoom > 15) {
  //             mapRef.current.setZoom(15);
  //           }
  //         });
  //       }
  //     }
  //   }
  // }, [selectedElectorate]);

  const handleSaveMapRestrictions = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.put('/config/app-config', {
        mapRestrictions,
      });
      setSuccess('Map restrictions updated successfully');
    } catch (err) {
      console.error('Error updating map restrictions:', err);
      setError('Failed to update map restrictions');
    } finally {
      setLoading(false);
    }
  };

  const handleBoundsChange = (bound, value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;

    setMapRestrictions(prev => ({
      ...prev,
      bounds: {
        ...prev.bounds,
        [bound]: numValue,
      },
    }));
  };

  const handleAddCustomArea = () => {
    setShowForm(true);
    // Scroll to the form after a brief delay to ensure it's rendered
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const data = {
        ...formData,
        center_lat: parseFloat(formData.center_lat) || null,
        center_lng: parseFloat(formData.center_lng) || null,
        zoom_level: parseInt(formData.zoom_level) || 12,
      };

      if (editingArea) {
        await api.updateAreaConfig(editingArea.id, data);
        setSuccess('Area configuration updated successfully');
      } else {
        await api.createAreaConfig(data);
        setSuccess('Area configuration created successfully');
      }

      setShowForm(false);
      setEditingArea(null);
      loadAreas();
    } catch (err) {
      console.error('Error saving area config:', err);
      setError('Failed to save area configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = area => {
    setEditingArea(area);
    setFormData({
      area_type: area.area_type,
      area_name: area.area_name,
      area_code: area.area_code || '',
      coordinates: area.coordinates || [],
      center_lat: area.center_lat || '',
      center_lng: area.center_lng || '',
      zoom_level: area.zoom_level || 12,
      border_strictness: area.border_strictness || 'medium',
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingArea(null);
  };

  const openMapPicker = () => setShowMapPicker(true);
  const closeMapPicker = () => setShowMapPicker(false);

  const handleMapPickerConfirm = location => {
    setFormData(prev => ({
      ...prev,
      center_lat: location.lat.toString(),
      center_lng: location.lng.toString(),
    }));
    setShowMapPicker(false);
  };

  // Get unique electorate names and a map of name -> first feature
  const electorateNameToFeature = {};
  electorates.forEach(f => {
    const name = f.properties.vic_stat_2;
    if (name && !electorateNameToFeature[name]) {
      electorateNameToFeature[name] = f;
    }
  });
  let uniqueElectorateNames = Object.keys(electorateNameToFeature);
  // Filter by search
  if (electorateSearch.trim()) {
    uniqueElectorateNames = uniqueElectorateNames.filter(name =>
      name.toLowerCase().includes(electorateSearch.trim().toLowerCase()),
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Area Configuration</h2>
          <p className="text-gray-600 mt-1">
            Manage Victorian State electorates, custom areas, map restrictions, and heatmap display
          </p>
        </div>
        <button
          onClick={handleAddCustomArea}
          className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Custom Area
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
          <X className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
          <Globe className="w-5 h-5 text-green-500" />
          <span className="text-green-700">{success}</span>
        </div>
      )}

      {/* Active Area Display - always reflects selectedElectorate */}
      {selectedElectorate && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Globe className="w-6 h-6 text-blue-600" />
              <div>
                <h3 className="font-semibold text-blue-900">Active Area</h3>
                <p className="text-blue-700">{selectedElectorate.properties.vic_stat_2}</p>
                {selectedElectorate.properties.vic_stat_1 && (
                  <p className="text-sm text-blue-600">
                    Code: {selectedElectorate.properties.vic_stat_1}
                  </p>
                )}
              </div>
            </div>
            <div className="text-right">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Active
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Electorate selection with combobox/autocomplete */}
      <div className="mb-4">
        <label
          className="block text-sm font-medium text-gray-700 mb-1"
          htmlFor="electorate-combobox"
        >
          Victorian State Electorate
        </label>
        <div className="relative">
          <input
            id="electorate-combobox"
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Type to search..."
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={electorateSearch.length > 0}
            aria-controls="electorate-listbox"
            value={electorateSearch}
            onChange={e => {
              setElectorateSearch(e.target.value);
            }}
            onFocus={e => {
              if (!electorateSearch && selectedElectorate?.properties?.vic_stat_2) {
                setElectorateSearch(selectedElectorate.properties.vic_stat_2);
              }
            }}
            autoComplete="off"
          />
          {electorateSearch && uniqueElectorateNames.length > 0 && (
            <ul
              id="electorate-listbox"
              role="listbox"
              className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
            >
              {uniqueElectorateNames.map((name, i) => (
                <li
                  key={name}
                  role="option"
                  aria-selected={selectedElectorate?.properties?.vic_stat_2 === name}
                  className={`px-3 py-2 cursor-pointer hover:bg-purple-100 ${selectedElectorate?.properties?.vic_stat_2 === name ? 'bg-purple-50 font-semibold' : ''}`}
                  onMouseDown={() => {
                    setElectorateSearch(name);
                    const found = electorateNameToFeature[name];
                    setSelectedElectorate(found);
                    if (found && found.geometry && found.geometry.coordinates) {
                      const centroid = getPolygonCentroid(found.geometry.coordinates);
                      setMapCenter(centroid);
                    }
                    if (onActiveAreaChange && found) {
                      onActiveAreaChange(found);
                    }
                  }}
                >
                  {name}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {/* Map with boundary overlay */}
      <div className="w-full h-[400px] rounded-xl overflow-hidden border border-gray-200 mb-6 relative">
        {isLoaded && (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={mapCenter}
            zoom={mapZoom}
            onLoad={map => {
              mapRef.current = map;
            }}
            onClick={e => {
              if (mapRef.current && e.latLng) {
                const lat = typeof e.latLng.lat === 'function' ? e.latLng.lat() : e.latLng.lat;
                const lng = typeof e.latLng.lng === 'function' ? e.latLng.lng() : e.latLng.lng;
                setMapCenter({ lat, lng });
              }
            }}
            onZoomChanged={() => {
              if (mapRef.current) {
                setMapZoom(mapRef.current.getZoom());
              }
            }}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              clickableIcons: false,
              gestureHandling: 'cooperative',
              zoomControl: true,
              mapTypeId: 'roadmap',
              styles: [
                {
                  featureType: 'poi',
                  elementType: 'labels',
                  stylers: [{ visibility: 'off' }],
                },
                {
                  featureType: 'poi.business',
                  elementType: 'labels',
                  stylers: [{ visibility: 'off' }],
                },
                {
                  featureType: 'transit',
                  elementType: 'labels',
                  stylers: [{ visibility: 'off' }],
                },
              ],
            }}
          >
            {selectedElectorate &&
              selectedElectorate.geometry &&
              (() => {
                return (
                  <Polygon
                    paths={selectedElectorate.geometry.coordinates[0].map(([lng, lat]) => ({
                      lat,
                      lng,
                    }))}
                    options={{
                      fillColor: '#1976d2',
                      fillOpacity: 0.15,
                      strokeColor: '#1976d2',
                      strokeOpacity: 0.8,
                      strokeWeight: 2,
                    }}
                  />
                );
              })()}
          </GoogleMap>
        )}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center w-full px-4">
          <div className="flex gap-2 mb-2">
            <button
              type="button"
              className="px-4 py-2 bg-blue-600 text-white rounded-md shadow hover:bg-blue-700 transition-colors"
              onClick={async () => {
                try {
                  if (!mapRef.current) throw new Error('Map not loaded');
                  const centerObj = mapRef.current.getCenter();
                  const zoomVal = mapRef.current.getZoom();
                  const center = { lat: centerObj.lat(), lng: centerObj.lng() };
                  // Load current config, update map defaults, and save
                  const configRes = await api.getAppConfig();
                  const config = configRes.config || {};
                  const updatedConfig = {
                    ...config,
                    defaultMapCenter: center,
                    defaultMapZoom: zoomVal,
                  };
                  await api.updateAppConfig(updatedConfig);
                  setSuccess('Default map center and zoom saved!');
                  setTimeout(() => setSuccess(''), 2000);
                  await reloadDefaults();
                  setMapCenter(defaultCenter);
                  setMapZoom(defaultZoom);
                } catch (err) {
                  setError('Failed to save map defaults');
                  setTimeout(() => setError(''), 2000);
                }
              }}
            >
              Set as Default Center & Zoom
            </button>
            <button
              type="button"
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md shadow hover:bg-gray-300 transition-colors"
              onClick={() => {
                setMapCenter(defaultCenter);
                setMapZoom(defaultZoom);
              }}
            >
              Reset to Default
            </button>
          </div>
          <span className="text-xs text-gray-500">
            Click the map to set center. Use zoom controls. Then save or reset.
          </span>
        </div>
      </div>

      {/* Area Configuration Form */}
      {showForm && (
        <div ref={formRef} className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingArea ? 'Edit Area Configuration' : 'Add Custom Area'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Area Type</label>
                <select
                  value={formData.area_type}
                  onChange={e => setFormData({ ...formData, area_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  disabled={editingArea?.area_type === 'electorate'}
                >
                  <option value="electorate">Victorian Electorate</option>
                  <option value="custom">Custom Area</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Area Name *</label>
                <input
                  type="text"
                  value={formData.area_name}
                  onChange={e => setFormData({ ...formData, area_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Area Code</label>
                <input
                  type="text"
                  value={formData.area_code}
                  onChange={e => setFormData({ ...formData, area_code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  maxLength={10}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Border Strictness
                </label>
                <select
                  value={formData.border_strictness}
                  onChange={e => setFormData({ ...formData, border_strictness: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="strict">Strict</option>
                  <option value="medium">Medium</option>
                  <option value="fuzzy">Fuzzy</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Center Latitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.center_lat}
                  onChange={e => setFormData({ ...formData, center_lat: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="-37.735433"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Center Longitude
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="any"
                    value={formData.center_lng}
                    onChange={e => setFormData({ ...formData, center_lng: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="144.9631"
                  />
                  <button
                    type="button"
                    onClick={openMapPicker}
                    className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    title="Pick center point on map"
                  >
                    <MapPin className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Zoom Level</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={formData.zoom_level}
                  onChange={e => setFormData({ ...formData, zoom_level: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Saving...' : editingArea ? 'Update Area' : 'Create Area'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Map Restrictions Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Settings className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Map Restrictions</h3>
          </div>
          <button
            onClick={() => setShowMapRestrictions(!showMapRestrictions)}
            className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
          >
            {showMapRestrictions ? (
              <EyeOff className="w-4 h-4 mr-1" />
            ) : (
              <Eye className="w-4 h-4 mr-1" />
            )}
            {showMapRestrictions ? 'Hide Settings' : 'Show Settings'}
          </button>
        </div>

        {showMapRestrictions && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Enable Map Restrictions</span>
              <button
                onClick={() => setMapRestrictions(prev => ({ ...prev, enabled: !prev.enabled }))}
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  mapRestrictions.enabled
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {mapRestrictions.enabled ? 'Enabled' : 'Disabled'}
              </button>
            </div>

            {mapRestrictions.enabled && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Restriction Zoom Level
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    step="0.1"
                    value={mapRestrictions.zoomLevel}
                    onChange={e =>
                      setMapRestrictions(prev => ({
                        ...prev,
                        zoomLevel: parseFloat(e.target.value) || 13,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="strictBounds"
                    checked={mapRestrictions.strictBounds}
                    onChange={e =>
                      setMapRestrictions(prev => ({
                        ...prev,
                        strictBounds: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="strictBounds" className="ml-2 block text-sm text-gray-700">
                    Strict Bounds (prevent panning outside bounds)
                  </label>
                </div>

                <div className="space-y-3">
                  <h5 className="text-sm font-medium text-gray-700">Boundary Coordinates</h5>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        North Boundary
                      </label>
                      <input
                        type="number"
                        step="0.000001"
                        value={mapRestrictions.bounds.north}
                        onChange={e => handleBoundsChange('north', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        South Boundary
                      </label>
                      <input
                        type="number"
                        step="0.000001"
                        value={mapRestrictions.bounds.south}
                        onChange={e => handleBoundsChange('south', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        East Boundary
                      </label>
                      <input
                        type="number"
                        step="0.000001"
                        value={mapRestrictions.bounds.east}
                        onChange={e => handleBoundsChange('east', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        West Boundary
                      </label>
                      <input
                        type="number"
                        step="0.000001"
                        value={mapRestrictions.bounds.west}
                        onChange={e => handleBoundsChange('west', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-200">
                  <button
                    onClick={handleSaveMapRestrictions}
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Saving...' : 'Save Map Restrictions'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <HeatmapConfigManager />

      {showMapPicker && (
        <MapPicker
          isOpen={showMapPicker}
          onClose={closeMapPicker}
          onConfirm={handleMapPickerConfirm}
          initialLocation={
            formData.center_lat && formData.center_lng
              ? {
                  lat: parseFloat(formData.center_lat),
                  lng: parseFloat(formData.center_lng),
                  address: formData.area_name || 'Selected location',
                  coords: `${formData.center_lat}, ${formData.center_lng}`,
                }
              : null
          }
          activeArea={activeArea}
          description=""
        />
      )}
    </div>
  );
}

export default AreaConfigManager;
